import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Alert,
  DialogContent,
  DialogActions,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
} from "@mui/material";
import axios, { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useMutation } from "react-query";
import * as yup from "yup";
import { queryClient } from "../App";
import { API_URL } from "../consts";
import { Colori, Tariffe_Definizioni } from "../prismaTypes";
import { Loading } from "../ui/Loading";
import { getString } from "../utils/getString";

type Inputs = {
  id?: number;
  codice: string | null;
  nome: string;
  descrizione: string | null;
  coloriId: number;
  default: boolean;
  luciStatus: boolean;
  riscaldamentoStatus: boolean;
  deleted: boolean;
};
const defaultValues = {
  codice: "",
  nome: "",
  descrizione: "",
  coloriId: 0,
  default: false,
  luciStatus: false,
  riscaldamentoStatus: false,
  deleted: false,
};
const schema = yup
  .object({
    coloriId: yup.number().required().min(1),
    descrizione: yup.string(),
    default: yup.boolean(),
    luciStatus: yup.boolean(),
    riscaldamentoStatus: yup.boolean(),
    deleted: yup.boolean(),
    nome: yup.string().required(),
    codice: yup.string().required(),
  })
  .required();

export function TariffeDefinizione(props: { id?: number; closeDialog: () => void }) {
  let { id, closeDialog } = props;

  const [loaded, setLoaded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { refetch } = useQuery<{ definizione: Tariffe_Definizioni }, AxiosError>(
    ["tariffe-definizioni", id],
    () => axios.get(API_URL + "tariffe-definizioni/get/" + id).then((res) => res.data),
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        queryClient.invalidateQueries();
        const { definizione } = data;
        setLoaded(true);

        setValue("codice", getString(definizione.codice));
        setValue("nome", getString(definizione.nome));
        setValue("descrizione", getString(definizione.descrizione));
        setValue("coloriId", definizione.coloriId);
        setValue("default", definizione.default);
        setValue("luciStatus", definizione.luciStatus);
        setValue("riscaldamentoStatus", definizione.riscaldamentoStatus);
        setValue("deleted", definizione.deleted);
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar("Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { error, mutate } = useMutation(
    (data: Inputs) => {
      return axios.post(API_URL + "tariffe-definizioni/create", data);
    },
    {
      onSuccess: (data) => {
        const { id } = data.data;
        if (id) {
          queryClient.invalidateQueries();
          enqueueSnackbar("Tariffa Creata", { variant: "success" });
          closeDialog();
        } else enqueueSnackbar("Si è verificato un errore!", { variant: "error" });
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { mutate: mutateUpdate } = useMutation(
    (data: Inputs) => {
      return axios.post(API_URL + "tariffe-definizioni/update", { ...data });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Tariffa Aggiornata", { variant: "success" });
        closeDialog();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({
    mode: "all",
    resolver: yupResolver(schema),
  });
  const onSubmit = (data: Inputs) => {
    if (id !== undefined) mutateUpdate({ ...data, id });
    else mutate({ ...data });
  };

  useEffect(() => {
    setLoaded(false);
    if (id === undefined) {
      reset(defaultValues);
      setLoaded(true);
    } else refetch();
  }, [reset, setValue, id, refetch]);

  const [animations, setAnimations] = useState(false);
  useEffect(() => {
    let timeout = 0;
    if (loaded) {
      timeout = window.setTimeout(() => setAnimations(true), 1);
    }
    return () => {
      if (timeout !== 0) clearTimeout(timeout);
    };
  }, [loaded]);

  let errorMsg = error?.response?.data?.error ?? "";

  let title = id === undefined ? "Crea Tariffa" : "Modifica Tariffa";

  const { data: colori } = useQuery<{ colori: Colori[] }, AxiosError>(
    ["colori"],
    () => axios.get(API_URL + "tipologie-uso/colori").then((res) => res.data),
    {
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  if (!loaded) return <Loading />;

  return (
    <Box className={animations ? "" : "noAnimation"}>
      <DialogTitle>{title}</DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container columnSpacing={1}>
            <Grid item xs={12}>
              <Controller
                name="default"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("default", v.target.checked)}
                      />
                    }
                    label="Default"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="codice"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    variant="standard"
                    label="Codice"
                    error={Boolean(errors.codice)}
                    helperText={errors.codice?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Controller
                name="nome"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Nome"
                    error={Boolean(errors.nome)}
                    helperText={errors.nome?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="descrizione"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Descrizione"
                    error={Boolean(errors.descrizione)}
                    helperText={errors.descrizione?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={10} lg={9}>
              <Controller
                name="coloriId"
                control={control}
                defaultValue={1}
                render={({ field }) => (
                  <FormControl
                    {...field}
                    fullWidth
                    required
                    variant="standard"
                    error={Boolean(errors.coloriId)}
                  >
                    <InputLabel>Colore</InputLabel>
                    <Select
                      label="Colore"
                      value={field.value}
                      onChange={(e) => setValue("coloriId", parseInt("" + e.target.value))}
                    >
                      {colori?.colori
                        .sort((a, b) => a.id - b.id)
                        .map((col) => (
                          <MenuItem key={col.id} value={col.id} sx={{ bgcolor: col.esadecimale }}>
                            {col.nome}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>{errors.coloriId?.message ?? " "}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="luciStatus"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("luciStatus", v.target.checked)}
                      />
                    }
                    label="Luci"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="riscaldamentoStatus"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("riscaldamentoStatus", v.target.checked)}
                      />
                    }
                    label="Riscaldamento"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button sx={{ mt: 2 }} onClick={closeDialog}>
            Chiudi
          </Button>

          {!id && (
            <Button type="submit" sx={{ mt: 2 }}>
              Crea Tariffa
            </Button>
          )}
          {id && (
            <Button type="submit" sx={{ mt: 2 }}>
              Modifica Tariffa
            </Button>
          )}
        </DialogActions>
      </form>
      {errorMsg && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMsg}
        </Alert>
      )}
    </Box>
  );
}
